import type {CommentDto} from '@domain/comments'
import type {ScheduleItem} from '@domain/schedule'
import {roundTo15MinIntervalStart} from '@hconnect/common/utils'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {DeleteOutline} from '@mui/icons-material'
import {Button} from '@mui/material'
import {useSelectedScheduleItemId} from '@widgets/planning-schedule-chart'
import {useCurrentTimeRounded} from '@widgets/stock-card'
import moment from 'moment-timezone'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../consts'

import type {DeleteCommentParams} from './EditScheduleItemForm'

interface DeleteScheduleItemButtonProps {
  scheduleItem: ScheduleItem
  isReadOnly: boolean
  comment?: CommentDto
  deleteScheduleItem?: (item: ScheduleItem) => void
  deleteComment?: (
    params: DeleteCommentParams,
    options?: {onSuccess?: () => void; onError?: () => void}
  ) => void
}

export const DeleteScheduleItemButton: React.FC<DeleteScheduleItemButtonProps> = ({
  scheduleItem,
  isReadOnly,
  comment,
  deleteScheduleItem,
  deleteComment
}) => {
  const timezoneId = useUpmPlantTimezone()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {setScheduleItemId} = useSelectedScheduleItemId()

  const nowRounded = useCurrentTimeRounded({timezoneId, roundingFn: roundTo15MinIntervalStart})

  const handleDeleteComment = (comment: CommentDto) => {
    deleteComment?.({
      commentCategoryInstanceId: scheduleItem.id,
      commentId: comment.commentId
    })
  }

  const handleDeleteScheduleItem = () => {
    setScheduleItemId(undefined)
    deleteScheduleItem?.(scheduleItem)
    if (comment) {
      handleDeleteComment(comment)
    }
  }

  const isDeleteButtonDisabled = isReadOnly || moment.utc(scheduleItem.start).isBefore(nowRounded)

  return (
    <Button
      {...dataTestId('schedule_edit_delete')}
      startIcon={<DeleteOutline color="error" />}
      color="secondary"
      onClick={handleDeleteScheduleItem}
      disabled={isDeleteButtonDisabled}
    >
      {t('planning.deletePlan')}
    </Button>
  )
}
